import React, { createContext, useContext, useEffect, useState } from "react";
import {
    CreateListItemPayload,
    ListDetail,
    ListItem,
    ListItemType,
} from "modules/lists/types/lists";
import * as listService from "modules/lists/services/listService";

// ──────────────────────────────────────────────────────────────────

interface ListsContextValue {
    lists: ListDetail[];
    isLoading: boolean;
    /**
     * Creates a new list via the API and returns the newly created ListDetail.
     * @returns The newly created `ListDetail`
     */
    createList: (name: string, description: string) => Promise<ListDetail>;
    /** Removes a list by id via the API. */
    deleteList: (listId: string) => Promise<void>;
    /** Updates the name and description of an existing list via the API. */
    updateList: (listId: string, name: string, description: string) => Promise<void>;
    /** Adds a new item to an existing list via the API. */
    addItem: (listId: string, data: CreateListItemPayload) => Promise<void>;
    /** Replaces the fields of an existing item (local state only). */
    editItem: (listId: string, itemId: string, data: CreateListItemPayload) => void;
    /**
     * Merges a server-authoritative ListItem into local state.
     * Use this after a successful backend update to reconcile type, linkedProductName,
     * and catalogProductSku with the values the server resolved.
     */
    syncItem: (listId: string, item: ListItem) => void;
    /** Soft-deletes an item from a list via the API. */
    deleteItem: (listId: string, itemId: string) => Promise<void>;
    /**
     * Fetches full detail (including items) for a list and merges it into context.
     * No-op if the list is already hydrated (items.length > 0).
     */
    loadListDetail: (listId: string) => Promise<void>;
}

const ListsContext = createContext<ListsContextValue | null>(null);

/**
 * Provides shared lists state and CRUD handlers to all list-related pages.
 * Must wrap any component that calls `useLists`.
 */
const ListsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [lists, setLists] = useState<ListDetail[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const loadLists = async () => {
            try {
                setIsLoading(true);
                const fetched = await listService.fetchLists();
                // Merge into existing state — preserves items already loaded by loadListDetail
                // (handles direct-URL navigation where detail loads before the list index)
                setLists((prev) => {
                    const prevMap = new Map(prev.map((l) => [l.id, l]));
                    return fetched.map((l) => ({
                        ...l,
                        items: prevMap.get(l.id)?.items ?? [],
                        sampleItems: l.sampleItems,
                    }));
                });
            } catch {
                // error silenced — list page handles empty state
            } finally {
                setIsLoading(false);
            }
        };
        loadLists();
    }, []);

    const createList = async (name: string, description: string): Promise<ListDetail> => {
        const created = await listService.createList(name, description);
        const newDetail: ListDetail = { ...created, items: [], itemCount: 0 };
        setLists((prev) => [...prev, newDetail]);
        return newDetail;
    };

    const deleteList = async (listId: string): Promise<void> => {
        await listService.deleteList(listId);
        setLists((prev) => prev.filter((l) => l.id !== listId));
    };

    const updateList = async (listId: string, name: string, description: string): Promise<void> => {
        const updated = await listService.updateList(listId, { name, description });
        setLists((prev) =>
            prev.map((l) => (l.id === listId ? { ...l, ...updated } : l))
        );
    };

    const addItem = async (listId: string, data: CreateListItemPayload): Promise<void> => {
        const newItem = await listService.addItem(listId, data);
        setLists((prev) =>
            prev.map((l) => {
                if (l.id !== listId) return l;
                return {
                    ...l,
                    items: [...l.items, newItem],
                    itemCount: l.itemCount + 1,
                    updatedAt: new Date().toISOString(),
                };
            })
        );
    };

    const editItem = (listId: string, itemId: string, data: CreateListItemPayload) => {
        // catalogProductId can be ephemeral (GCR-generated) or absent when linking via picker.
        // catalogProductSku (hashed_sku) is the canonical catalog key — use it as a fallback
        // so optimistic state reflects LINKED even when the id is null.
        const type = (data.catalogProductId || data.catalogProductSku)
            ? ListItemType.LINKED
            : ListItemType.FREE;
        setLists((prev) =>
            prev.map((l) =>
                l.id === listId
                    ? {
                        ...l,
                        updatedAt: new Date().toISOString(),
                        items: l.items.map((item) =>
                            item.id === itemId
                                ? {
                                    ...item,
                                    ...data,
                                    catalogProductId: data.catalogProductId ?? null,
                                    unit: data.unit ?? null,
                                    notes: data.notes ?? null,
                                    type,
                                }
                                : item
                        ),
                    }
                    : l
            )
        );
    };

    const deleteItem = async (listId: string, itemId: string): Promise<void> => {
        await listService.deleteItem(listId, itemId);
        setLists((prev) =>
            prev.map((l) =>
                l.id === listId
                    ? {
                        ...l,
                        itemCount: l.itemCount - 1,
                        updatedAt: new Date().toISOString(),
                        items: l.items.filter((item) => item.id !== itemId),
                    }
                    : l
            )
        );
    };

    /**
     * Merges a server-authoritative ListItem into local state.
     * Call this after a successful backend save (e.g. updateItem) to reconcile
     * `type`, `linkedProductName`, and `catalogProductSku` with what the server resolved.
     * Safe to call even if the item is not found (no-op).
     *
     * @param listId UUID of the parent list.
     * @param item The updated ListItem returned by the backend.
     */
    const syncItem = (listId: string, item: ListItem) => {
        setLists((prev) =>
            prev.map((l) =>
                l.id === listId
                    ? {
                        ...l,
                        items: l.items.map((existing) =>
                            existing.id === item.id ? item : existing
                        ),
                    }
                    : l
            )
        );
    };

    /**
     * Loads full list detail from the API and merges items into context state.
     * Always re-fetches to ensure fresh data on navigation.
     *
     * @param listId UUID of the list to hydrate.
     */
    const loadListDetail = async (listId: string): Promise<void> => {
        try {
            const detail = await listService.fetchListDetail(listId);
            setLists((prev) => {
                const exists = prev.some((l) => l.id === listId);
                if (exists) {
                    return prev.map((l) =>
                        l.id === listId
                            ? { ...l, ...detail, sampleItems: l.sampleItems }
                            : l
                    );
                }
                // List not in state yet — direct URL navigation before fetchLists completed
                return [...prev, { ...detail, sampleItems: [] }];
            });
        } catch {
            // silenced — the page will show empty state
        }
    };

    return (
        <ListsContext.Provider
            value={{ lists, isLoading, createList, deleteList, updateList, addItem, editItem, syncItem, deleteItem, loadListDetail }}
        >
            {children}
        </ListsContext.Provider>
    );
};

/**
 * Hook to consume the lists context.
 * Must be used inside a `ListsProvider`.
 * @throws Error if used outside of `ListsProvider`
 */
const useLists = () => {
    const ctx = useContext(ListsContext);
    if (!ctx) throw new Error("useLists must be used within ListsProvider");
    return ctx;
};

export { ListsProvider, useLists };
